import { getAuth } from "firebase/auth";
import { logger } from '../utils/logger';
import { authAxios } from '../utils/apiConfig';

const aiAxios = authAxios;

/**
 * Generate ideal ad mockup based on competitor ads analysis
 * @param {string} vertical - Business vertical (e.g., "plumbers", "dentists")
 * @param {Array} competitorAds - Array of competitor ad objects
 * @param {Array} targetKeywords - Target keywords for the ad
 * @param {string} businessContext - Optional business context
 * @param {boolean} useSuccessScoring - Whether to filter ads by success score (default: true)
 * @param {number} minSuccessScore - Minimum success score threshold (default: 60)
 * @returns {Promise<Object>} - Generated ideal ad data with success scoring metadata
 */
export async function generateIdealAd(
  vertical,
  competitorAds,
  targetKeywords = [],
  businessContext = null,
  useSuccessScoring = true,
  minSuccessScore = 60
) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const response = await aiAxios.post('/ai/generate-ideal-ad', {
      vertical,
      competitorAds,
      targetKeywords,
      businessContext,
      useSuccessScoring,
      minSuccessScore,
    }, {
      params: {
        user: user.email,
      }
    });

    return response.data;
  } catch (error) {
    logger.error("Error in generateIdealAd:", error);
    if (error.response) {
      logger.error("Server response:", error.response.data);
      throw new Error(error.response.data.detail || error.response.data.message || 'Server error');
    }
    throw error;
  }
}

/**
 * Score ads for success metrics
 * @param {Array} ads - Array of ad objects to score
 * @param {number} minScore - Minimum score threshold (default: 60)
 * @returns {Promise<Object>} - Scored ads with breakdown and summary
 */
export async function scoreAds(ads, minScore = 60) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const response = await aiAxios.post('/ai/score-ads', {
      ads,
      min_score: minScore,
    }, {
      params: {
        user: user.email,
      }
    });

    return response.data;
  } catch (error) {
    logger.error("Error in scoreAds:", error);
    if (error.response) {
      logger.error("Server response:", error.response.data);
      throw new Error(error.response.data.detail || error.response.data.message || 'Server error');
    }
    throw error;
  }
}

/**
 * Analyze competitor ads and extract patterns
 * @param {Array} ads - Array of ad objects
 * @returns {Object} - Analysis results
 */
export function analyzeCompetitorAds(ads) {
  if (!ads || ads.length === 0) {
    return {
      totalAds: 0,
      avgTitleLength: 0,
      avgBodyLength: 0,
      commonWords: [],
      topKeywords: [],
      positionDistribution: {},
    };
  }

  // Calculate averages
  const avgTitleLength = Math.round(
    ads.reduce((sum, ad) => sum + (ad.title?.length || 0), 0) / ads.length
  );
  const avgBodyLength = Math.round(
    ads.reduce((sum, ad) => sum + (ad.body?.length || 0), 0) / ads.length
  );

  // Extract common words from titles and bodies
  const wordCounts = {};
  const stopWords = new Set(['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might', 'must', 'can', 'this', 'that', 'these', 'those', 'your', 'our', 'we', 'you', 'they', 'it', 'its']);
  
  ads.forEach(ad => {
    const text = `${ad.title || ''} ${ad.body || ''}`.toLowerCase();
    const words = text.match(/\b[a-z]{3,}\b/g) || [];
    words.forEach(word => {
      if (!stopWords.has(word)) {
        wordCounts[word] = (wordCounts[word] || 0) + 1;
      }
    });
  });

  const commonWords = Object.entries(wordCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 20)
    .map(([word, count]) => ({ word, count }));

  // Extract top keywords
  const keywordCounts = {};
  ads.forEach(ad => {
    if (ad.keywords && Array.isArray(ad.keywords)) {
      ad.keywords.forEach(kw => {
        keywordCounts[kw] = (keywordCounts[kw] || 0) + 1;
      });
    }
  });

  const topKeywords = Object.entries(keywordCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 15)
    .map(([keyword, count]) => ({ keyword, count }));

  // Position distribution
  const positionDistribution = {};
  ads.forEach(ad => {
    const pos = ad.position || 'Unknown';
    positionDistribution[pos] = (positionDistribution[pos] || 0) + 1;
  });

  return {
    totalAds: ads.length,
    avgTitleLength,
    avgBodyLength,
    commonWords,
    topKeywords,
    positionDistribution,
  };
}

// Export all functions as default object
const aiAdService = {
  generateIdealAd,
  scoreAds,
  analyzeCompetitorAds,
};

export default aiAdService;
